/* Desktop thumbnail paging. Media selection stays owned by native MediaGallery. */
(() => {
  class LunyaGalleryThumbnails extends HTMLElement {
    constructor() {
      super();
      this.frame = null;
      this.onUpdate = this.scheduleUpdate.bind(this);
      this.onScroll = this.updateButtons.bind(this);
      this.onClick = this.handleClick.bind(this);
      this.onFocus = this.handleFocus.bind(this);
    }

    connectedCallback() {
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (!this.isConnected || !this.closest('.lunya-brand.template-product')
          || this.closest('.quickbuy-container, .js-quickbuy')) return;
        this.gallery = this.closest('media-gallery');
        this.slider = this.querySelector('.thumbnails .slider');
        this.previous = this.querySelector('button[name="prev"]');
        this.next = this.querySelector('button[name="next"]');
        if (!this.gallery || !this.slider || !this.previous || !this.next) return;
        this.desktop = matchMedia('(min-width: 1041px)');
        this.motion = matchMedia('(prefers-reduced-motion: reduce)');
        this.addEventListener('click', this.onClick);
        this.addEventListener('focusin', this.onFocus);
        this.slider.addEventListener('scroll', this.onScroll, { passive: true });
        this.gallery.addEventListener('on:media-gallery:change', this.onUpdate);
        this.desktop.addEventListener('change', this.onUpdate);
        this.resizeObserver = new ResizeObserver(this.onUpdate);
        this.resizeObserver.observe(this);
        this.mutationObserver = new MutationObserver(this.onUpdate);
        this.mutationObserver.observe(this.slider, { subtree: true, childList: true, attributes: true, attributeFilter: ['hidden'] });
        this.setAttribute('data-enhanced', '');
        this.update();
      });
    }

    disconnectedCallback() {
      if (this.frame !== null) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('focusin', this.onFocus);
      this.slider?.removeEventListener('scroll', this.onScroll);
      this.gallery?.removeEventListener('on:media-gallery:change', this.onUpdate);
      this.desktop?.removeEventListener('change', this.onUpdate);
      this.resizeObserver?.disconnect();
      this.mutationObserver?.disconnect();
      this.removeAttribute('data-enhanced');
      this.removeAttribute('data-overflow');
      this.style.removeProperty('--lunya-thumbnail-height');
    }

    scheduleUpdate() {
      if (this.frame !== null || !this.isConnected) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    update() {
      this.items = [...this.slider.querySelectorAll('.slider__item:not([hidden])')];
      if (!this.desktop.matches || !this.items.length) {
        this.removeAttribute('data-overflow');
        this.style.removeProperty('--lunya-thumbnail-height');
        return;
      }
      // Fit three opening thumbnails and any taller later thumbnail in full.
      const first = this.items[0].getBoundingClientRect();
      const last = this.items[Math.min(2, this.items.length - 1)].getBoundingClientRect();
      const height = `${Math.ceil(Math.max(last.bottom - first.top, ...this.items.map(item => item.getBoundingClientRect().height)))}px`;
      if (this.style.getPropertyValue('--lunya-thumbnail-height') !== height) {
        this.style.setProperty('--lunya-thumbnail-height', height);
      }
      this.toggleAttribute('data-overflow', this.slider.scrollHeight > this.slider.clientHeight + 1);
      const focused = this.slider.contains(document.activeElement)
        ? document.activeElement.closest('.slider__item:not([hidden])') : null;
      this.reveal(focused || this.querySelector('.slider__item.is-active:not([hidden])'));
      this.updateButtons();
    }

    updateButtons() {
      this.previous.disabled = this.slider.scrollTop <= 1;
      this.next.disabled = this.slider.scrollTop + this.slider.clientHeight >= this.slider.scrollHeight - 1;
    }

    handleClick(event) {
      const button = event.target.closest('.lunya-gallery-thumbnails__button');
      if (!button || button.disabled || !this.desktop.matches) return;
      this.slider.scrollBy({
        top: (button.name === 'next' ? 1 : -1) * this.slider.clientHeight,
        behavior: this.motion.matches ? 'instant' : 'smooth'
      });
    }

    handleFocus(event) {
      this.reveal(event.target.closest('.slider__item'));
    }

    reveal(item) {
      if (!item || !this.desktop.matches) return;
      const viewport = this.slider.getBoundingClientRect();
      const rect = item.getBoundingClientRect();
      if (rect.top < viewport.top) {
        this.slider.scrollTop += rect.top - viewport.top;
      } else if (rect.bottom > viewport.bottom) {
        this.slider.scrollTop += rect.bottom - viewport.bottom;
      }
    }
  }

  if (!customElements.get('lunya-gallery-thumbnails')) {
    customElements.define('lunya-gallery-thumbnails', LunyaGalleryThumbnails);
  }
})();
